'use client';

import { SectionPanel } from '@/components/ui/design-system';
import { cn } from '@/lib/utils';
import './search-explainer.css';

type StepKey = 'describe' | 'pipeline' | 'results';

interface TileSpec {
  key: StepKey;
  step: string;
  title: string;
  subtitle: string;
  panelTitle: string;
}

const TILES: TileSpec[] = [
  {
    key: 'describe',
    step: '01 Describe',
    panelTitle: 'Describe',
    title: "Describe what you're looking for",
    subtitle: 'Plain English — roles, companies, locations, or anything in between.',
  },
  {
    key: 'pipeline',
    step: '02 We search',
    panelTitle: 'We search',
    title: 'We run the 4-stage pipeline',
    subtitle: 'Understanding, companies, jobs, contacts — surfaced as we find them.',
  },
  {
    key: 'results',
    step: '03 Get results',
    panelTitle: 'Get results',
    title: 'A mixed, ranked feed',
    subtitle: 'Companies, jobs, and the right contacts grouped together for action.',
  },
];

interface SearchExplainerProps {
  className?: string;
  durationMs?: number;
}

export function SearchExplainer({ className, durationMs = 6000 }: SearchExplainerProps) {
  return (
    <section className={cn('mt-8 sm:mt-10', className)}>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
          How it works
        </p>
        <p className="hidden sm:block text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground/60">
          Describe · Search · Results
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
        {TILES.map((tile) => (
          <SectionPanel
            key={tile.key}
            title={tile.panelTitle}
            bleedOnMobile={false}
            contentClassName="bg-card"
          >
            <div className="search-explainer-tile">
              <div
                className="search-explainer"
                data-step={tile.key}
                data-active="true"
                style={{ ['--se-dur' as string]: `${durationMs}ms` }}
                aria-hidden="true"
              >
                <SceneFor stepKey={tile.key} />
              </div>
              <div className="se-step">
                <b>{tile.step.split(' ')[0]}</b>
                <span>{tile.step.split(' ').slice(1).join(' ')}</span>
              </div>
              <div className="se-title">{tile.title}</div>
              <div className="se-sub">{tile.subtitle}</div>
            </div>
          </SectionPanel>
        ))}
      </div>
    </section>
  );
}

function SceneFor({ stepKey }: { stepKey: StepKey }) {
  if (stepKey === 'describe') return <DescribeScene />;
  if (stepKey === 'pipeline') return <PipelineScene />;
  return <ResultsScene />;
}

function DescribeScene() {
  return (
    <svg viewBox="0 0 260 200" preserveAspectRatio="xMidYMid meet">
      <rect x="20" y="74" width="220" height="52" className="fill-paper line-mid" />
      <circle cx="38" cy="100" r="6" className="line-accent" />
      <line x1="42.5" y1="104.5" x2="48" y2="110" className="line-accent" strokeWidth="1.8" />

      <rect className="d-typed fill-accent" x="58" y="98" width="120" height="4" opacity="0.85" />
      <rect className="d-caret fill-accent" x="58" y="92" width="2" height="16" />

      <g className="d-chip k1">
        <rect x="34" y="34" width="62" height="20" className="fill-warm line-accent" />
        <circle cx="46" cy="44" r="3" className="fill-accent" />
        <path d="M46 38 L46 50" className="line-accent" strokeWidth="1.2" />
        <rect x="54" y="42" width="36" height="3" fill="#8a7f6c" />
      </g>

      <g className="d-chip k2">
        <rect x="108" y="30" width="58" height="20" className="fill-paper line-accent" />
        <rect x="116" y="38" width="8" height="6" className="fill-accent" />
        <rect x="116" y="36" width="8" height="2" className="fill-accent" />
        <rect x="128" y="40" width="32" height="3" fill="#8a7f6c" />
      </g>

      <g className="d-chip k3">
        <rect x="178" y="38" width="56" height="20" className="fill-warm line-accent" />
        <rect x="186" y="44" width="4" height="10" className="fill-accent" />
        <rect x="192" y="42" width="4" height="12" className="fill-accent" />
        <rect x="198" y="46" width="4" height="8" className="fill-accent" />
        <rect x="206" y="46" width="22" height="3" fill="#8a7f6c" />
      </g>

      <rect x="20" y="146" width="34" height="6" className="fill-warm line-accent" strokeWidth="0.8" />
      <rect x="60" y="146" width="40" height="6" fill="none" stroke="#b5ad9e" />
      <rect x="106" y="146" width="28" height="6" fill="none" stroke="#b5ad9e" />
    </svg>
  );
}

function PipelineScene() {
  const stages = [
    { y: 30, label: 'Understanding' },
    { y: 68, label: 'Companies' },
    { y: 106, label: 'Jobs' },
    { y: 144, label: 'Contacts' },
  ];
  return (
    <svg viewBox="0 0 260 200" preserveAspectRatio="xMidYMid meet">
      <line x1="40" y1="38" x2="40" y2="160" className="line" strokeDasharray="2 3" />

      {stages.map((stage, i) => {
        const cls = `s${i + 1}` as const;
        return (
          <g key={stage.label} className={`p-stage ${cls}`}>
            <circle cx="40" cy={stage.y + 8} r="9" className="fill-paper line-accent" />
            <circle className={`p-dot ${cls} fill-accent`} cx="40" cy={stage.y + 8} r="5" />
            <path
              className={`p-check ${cls} line-strong`}
              d={`M${36} ${stage.y + 8} L${39} ${stage.y + 11} L${44} ${stage.y + 5}`}
              stroke="#fff"
              strokeWidth="1.4"
              fill="none"
            />
            <rect x="58" y={stage.y + 2} width="170" height="12" className="fill-paper line-mid" />
            <rect className={`p-bar fill-warm ${cls}`} x="58" y={stage.y + 2} width="170" height="12" />
            <text
              x="64"
              y={stage.y + 11}
              fontFamily="IBM Plex Mono, ui-monospace, monospace"
              fontSize="8"
              fontWeight="700"
              fill="var(--primary, #7f1d1d)"
              letterSpacing="0.6"
            >
              {stage.label.toUpperCase()}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function ResultsScene() {
  return (
    <svg viewBox="0 0 260 200" preserveAspectRatio="xMidYMid meet">
      <g className="f-row r1">
        <rect x="18" y="22" width="224" height="42" className="fill-paper line-mid" />
        <rect x="26" y="30" width="26" height="26" className="fill-warm line-accent" />
        <rect x="30" y="34" width="6" height="18" className="fill-accent" />
        <rect x="38" y="34" width="6" height="18" className="fill-accent" opacity="0.7" />
        <rect x="46" y="34" width="2" height="18" className="fill-accent" opacity="0.5" />
        <rect x="60" y="32" width="84" height="4" fill="#8a7f6c" />
        <rect x="60" y="40" width="120" height="3" fill="#b5ad9e" />
        <rect x="60" y="46" width="92" height="3" fill="#b5ad9e" />
        <rect x="200" y="32" width="34" height="8" className="fill-warm line-accent" strokeWidth="0.8" />
        <circle className="f-pulse p1 fill-accent" cx="234" cy="56" r="3.5" />
      </g>

      <g className="f-row r2">
        <rect x="18" y="72" width="224" height="38" className="fill-paper line-mid" />
        <rect x="26" y="80" width="6" height="22" className="fill-accent" />
        <rect x="40" y="82" width="74" height="4" fill="#8a7f6c" />
        <rect x="40" y="90" width="118" height="3" fill="#b5ad9e" />
        <rect x="40" y="97" width="68" height="3" fill="#b5ad9e" />
        <rect x="194" y="82" width="40" height="6" className="fill-accent" opacity="0.85" />
        <rect x="194" y="92" width="28" height="3" fill="#b5ad9e" />
      </g>

      <g className="f-row r3">
        <rect x="38" y="118" width="204" height="34" className="fill-paper line-mid" />
        <line x1="32" y1="110" x2="32" y2="135" className="line-accent" />
        <line x1="32" y1="135" x2="44" y2="135" className="line-accent" />
        <circle cx="52" cy="135" r="7" className="fill-warm" stroke="#8a7f6c" strokeWidth="1" />
        <rect x="64" y="124" width="62" height="4" fill="#8a7f6c" />
        <rect x="64" y="132" width="92" height="3" fill="#b5ad9e" />
        <rect x="64" y="139" width="48" height="3" fill="#b5ad9e" />
        <rect x="194" y="128" width="40" height="14" className="fill-warm line-accent" strokeWidth="0.8" />
        <circle className="f-pulse p2 fill-accent" cx="234" cy="118" r="3" />
      </g>

      <rect x="18" y="162" width="224" height="20" fill="none" stroke="#dedad3" />
      <rect x="26" y="170" width="14" height="4" className="fill-accent" />
      <rect x="44" y="170" width="14" height="4" className="fill-accent" opacity="0.55" />
      <rect x="62" y="170" width="14" height="4" fill="#b5ad9e" />
      <text
        x="234"
        y="175"
        textAnchor="end"
        fontFamily="IBM Plex Mono, ui-monospace, monospace"
        fontSize="8"
        fontWeight="700"
        fill="#8a7f6c"
        letterSpacing="1"
      >
        24 RESULTS
      </text>
    </svg>
  );
}
